import { useId, useMemo, useRef, useState } from 'react';

import {
  useAppStoreConnection,
  useAppStoreStatus,
  useRemoveAppStoreConnection,
  useSaveAppStoreConnection,
  useTestAppStoreConnection,
  type AppStoreConnection,
} from '@/lib/app-store-connect';
import { saveMessage } from '@/lib/apps';
import { moment } from '@/lib/dates';
import { useSeeded } from '@/lib/seeded';

/**
 * The App Store Connect card on Settings › Connections, wired.
 *
 * Four values — Issuer ID, Key ID, Vendor number, the `.p8` — that used to
 * live only in `apps/api/.env.local`, and a server that refused to boot
 * without them. They still may: the environment is the fallback, and this
 * card says which of the two is in use. **What is saved here wins** — anh
 * Duy chốt it — because a form the server's file silently overrides is a form
 * that does nothing and says nothing.
 *
 * ⚠️ **The `.p8` is read in the browser and sent as text, once, through the
 * same session every other write uses.** It is not uploaded to the studio's
 * file storage — everything there has a public URL — and nothing about it
 * comes back: the server says `privateKeyStored` and the Key ID beside it,
 * which Apple prints in the open. A second save without choosing a file
 * keeps the one stored.
 *
 * ⚠️ **Test connection runs on the server.** It signs a token with the stored
 * key and asks Apple for one report; the key never comes to this browser to
 * be tested with. What it says is what the sync's log would say.
 */

/** What Apple calls each value, so somebody can match this against an env file. */
const FIELDS = [
  {
    key: 'issuerId',
    label: 'Issuer ID',
    placeholder: '00000000-0000-0000-0000-000000000000',
    hint: 'APPSTORE_ISSUER_ID · a UUID shown once above the key list.',
  },
  {
    key: 'keyId',
    label: 'Key ID',
    placeholder: 'ABCDE12345',
    hint: 'APPSTORE_KEY_ID · the 10-character id of the .p8 key.',
  },
  {
    key: 'vendorNumber',
    label: 'Vendor number',
    placeholder: '00000000',
    hint: 'APPSTORE_VENDOR_NUMBER · found on the Payments and Financial Reports page.',
  },
] as const;

/**
 * Keep a credential out of the browser's own stores: a box with no
 * `autoComplete` makes Chrome and Safari offer to save what is typed as a
 * password, and a spellchecker may send a box's contents to a remote service.
 */
const SECRET_FIELD = {
  autoComplete: 'new-password',
  spellCheck: false,
  autoCorrect: 'off',
  autoCapitalize: 'off',
} as const;

interface Draft {
  issuerId: string;
  keyId: string;
  vendorNumber: string;
  /** The chosen file's text, until saved. Never seeded from the server. */
  pem: string | null;
  pemName: string | null;
}

const draftOf = (saved: AppStoreConnection): Draft => ({
  issuerId: saved.issuerId,
  keyId: saved.keyId,
  vendorNumber: saved.vendorNumber,
  pem: null,
  pemName: null,
});

export function AppStoreConnectCard() {
  const connection = useAppStoreConnection();
  const status = useAppStoreStatus();
  const save = useSaveAppStoreConnection();
  const remove = useRemoveAppStoreConnection();
  const test = useTestAppStoreConnection();

  const [draft, setDraft] = useState<Draft | null>(null);
  /** Bumped on every save, so the form re-seeds whole and the file goes. */
  const [saves, setSaves] = useState(0);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const headingId = useId();
  const hints = useId();
  const keyNote = useId();
  const testResultId = useId();

  const baseline = useMemo(
    () => (connection.data ? draftOf(connection.data) : null),
    [connection.data],
  );
  useSeeded(baseline, setDraft, String(saves));

  if (connection.isPending || !draft) {
    return (
      <section aria-labelledby={headingId} className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <h2 id={headingId} className="eyebrow m-0">
          App Store Connect
        </h2>
        <p className="m-0 text-[13px]" style={{ color: 'var(--muted)' }}>
          {connection.isError ? saveMessage(connection.error) : 'Loading…'}
        </p>
      </section>
    );
  }

  const saved = connection.data!;
  const dirty =
    draft.issuerId !== saved.issuerId ||
    draft.keyId !== saved.keyId ||
    draft.vendorNumber !== saved.vendorNumber ||
    draft.pem !== null;
  const complete =
    draft.issuerId.trim() !== '' &&
    draft.keyId.trim() !== '' &&
    draft.vendorNumber.trim() !== '' &&
    // A stored key counts only if this server can open it: an unreadable
    // one would be "kept" by a save that the server then refuses.
    (draft.pem !== null || saved.source === 'settings');
  const inUse = saved.source === 'settings';
  /*
   * ⚠️ A stored key this server cannot open, whether or not the environment
   * is carrying the sync meanwhile. The first version said so only when
   * nothing was in use at all — with the environment set, the card showed
   * "via environment" over three full boxes and "Key stored", and somebody
   * reading it by the rule "what is saved here wins" would have believed
   * Settings was in charge.
   */
  const unreadable = saved.privateKeyStored && saved.source !== 'settings';
  const lastRead = status.data?.lastSync ?? null;

  const chooseFile = (file: File | null) => {
    setFileProblem(null);
    if (!file) return;
    /*
     * Read as text here, sent as text once. A `.p8` is a few hundred bytes of
     * PEM; a file much bigger is not one, and is refused before it is read
     * rather than sent to a server that would refuse it anyway.
     */
    if (file.size > 10_000) {
      setFileProblem(`${file.name} is ${Math.round(file.size / 1024)} KB — a .p8 is under 1 KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      if (!/BEGIN PRIVATE KEY/.test(text)) {
        setFileProblem(
          `${file.name} does not look like a .p8 — it should start with "-----BEGIN PRIVATE KEY-----".`,
        );
        return;
      }
      setDraft((current) => (current ? { ...current, pem: text, pemName: file.name } : current));
    };
    reader.onerror = () => setFileProblem(`${file.name} could not be read.`);
    reader.readAsText(file);
  };

  const onSave = () => {
    save.mutate(
      {
        issuerId: draft.issuerId,
        keyId: draft.keyId,
        vendorNumber: draft.vendorNumber,
        ...(draft.pem !== null ? { privateKeyPem: draft.pem } : {}),
      },
      {
        onSuccess: () => {
          setSaves((n) => n + 1);
          test.reset();
          if (fileInput.current) fileInput.current.value = '';
        },
      },
    );
  };

  return (
    <section aria-labelledby={headingId} className="panel flex flex-col gap-2.5 px-4 py-3.5">
      <span className="flex flex-wrap items-center gap-2.5">
        <h2 id={headingId} className="eyebrow m-0">
          App Store Connect
        </h2>
        {/*
          ⚠️ The pill says where the credentials in use come from, not merely
          that they exist. "Connected" alone would be true of a server whose
          env file carries them while this form sits empty — and the person
          reading this card is the one who has to know which of the two is in
          charge.
        */}
        {saved.source === 'settings' ? (
          <Pill tone="ok">connected · via Settings</Pill>
        ) : saved.source === 'environment' ? (
          <Pill tone="ok">connected · via environment</Pill>
        ) : null}
        {unreadable ? <Pill tone="warn">stored key unreadable</Pill> : null}
        {lastRead ? (
          <span className="ml-auto font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            last read {moment(lastRead.ranAt)} · {lastRead.outcome}
          </span>
        ) : saved.updatedAt ? (
          <span className="ml-auto font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            saved {moment(saved.updatedAt)}
            {saved.updatedByName ? ` by ${saved.updatedByName}` : ''}
          </span>
        ) : null}
      </span>

      <p className="m-0 text-[11.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
        These four values let Overview read downloads, proceeds and subscriber counts. Create the
        API key in App Store Connect under Users and Access → Integrations.
      </p>

      {saved.storageUnavailable ? (
        <Notice tone="warn">
          This server cannot store a private key yet: set{' '}
          <code className="font-mono">SECRETS_ENCRYPTION_KEY</code> in{' '}
          <code className="font-mono">apps/api/.env.local</code> and restart.
        </Notice>
      ) : null}

      {unreadable ? (
        <Notice tone="warn">
          A key is stored but this server cannot open it —{' '}
          <code className="font-mono">SECRETS_ENCRYPTION_KEY</code> has changed since it was saved
          {saved.source === 'environment' ? ', so the environment is in use meanwhile' : ''}. Choose
          the .p8 again to store it under the current key.
        </Notice>
      ) : saved.source === 'settings' && saved.environmentConfigured ? (
        <Notice tone="warn">
          The server's environment carries a set of these too. What is saved here is what is used;
          the environment is only read when this card is empty.
        </Notice>
      ) : saved.source === 'environment' ? (
        <Notice tone="warn">
          In use from the server's environment. Saving here takes over from it, without a restart.
        </Notice>
      ) : null}

      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        {FIELDS.map((field, index) => (
          <div key={field.key} className="flex min-w-0 flex-col gap-1.5">
            <label className="flex flex-col gap-1.5">
              <span className="field-label">{field.label}</span>
              <input
                value={draft[field.key]}
                placeholder={field.placeholder}
                aria-describedby={`${hints}-${index}`}
                onChange={(event) =>
                  setDraft((current) =>
                    current ? { ...current, [field.key]: event.target.value } : current,
                  )
                }
                {...SECRET_FIELD}
                className="field field-mono w-full"
              />
            </label>
            <span
              id={`${hints}-${index}`}
              className="text-[11px] leading-[1.45]"
              style={{ color: 'var(--dim)' }}
            >
              {field.hint}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="field-label">Private key · .p8</span>
        <div
          className="flex flex-wrap items-center gap-2.5 rounded-lg border border-dashed px-3 py-2.5"
          style={{ background: 'var(--in)', borderColor: 'var(--line2)' }}
        >
          <span
            aria-hidden="true"
            className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg"
            style={{
              background: 'var(--panel2)',
              border: '1px solid var(--line)',
              color: 'var(--muted)',
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            >
              <circle cx="7" cy="12" r="3" />
              <path d="M9.4 10.2l6-6M13.4 6.2l1.6 1.6M15.6 4l1.8 1.8" />
            </svg>
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="font-mono text-[11.5px]" style={{ color: 'var(--text)' }}>
              {draft.pemName
                ? `${draft.pemName} · not saved yet`
                : saved.privateKeyStored
                  ? `Key stored · ${saved.keyId || 'id unknown'}`
                  : 'No key stored'}
            </span>
            <span id={keyNote} className="text-[11px]" style={{ color: 'var(--dim)' }}>
              {fileProblem ??
                (unreadable
                  ? 'Stored, but it cannot be opened under the current key — choose the .p8 again.'
                  : saved.privateKeyStored
                    ? 'Stored encrypted. Choose a file only to replace it.'
                    : 'Apple lets you download the .p8 once. It is stored encrypted and never shown again here.')}
            </span>
          </span>
          <span className="ml-auto flex gap-2">
            <input
              ref={fileInput}
              type="file"
              accept=".p8,.pem,application/x-pem-file,text/plain"
              aria-label="Choose a .p8 file"
              aria-describedby={keyNote}
              className="sr-only"
              onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              disabled={saved.storageUnavailable}
              onClick={() => fileInput.current?.click()}
              className="btn-small focus-ring cursor-pointer disabled:cursor-default disabled:opacity-50"
            >
              {saved.privateKeyStored || draft.pemName ? 'Replace .p8' : 'Choose a .p8 file'}
            </button>
            {draft.pemName ? (
              <button
                type="button"
                onClick={() => {
                  setDraft((current) =>
                    current ? { ...current, pem: null, pemName: null } : current,
                  );
                  if (fileInput.current) fileInput.current.value = '';
                }}
                className="btn-small focus-ring cursor-pointer"
              >
                Undo
              </button>
            ) : null}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!dirty || !complete || save.isPending || saved.storageUnavailable}
            onClick={onSave}
            className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save connection'}
          </button>
          <button
            type="button"
            // Tests what is in use — stored or environment — so it lights whenever anything is.
            disabled={saved.source === 'none' || dirty || test.isPending}
            aria-describedby={test.data ? testResultId : undefined}
            onClick={() => test.mutate()}
            className="btn-outline focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {test.isPending ? 'Asking Apple…' : 'Test connection'}
          </button>
          {inUse || saved.privateKeyStored ? (
            <button
              type="button"
              disabled={remove.isPending || save.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Remove the stored App Store Connect credentials? The environment, if set, takes over.',
                  )
                ) {
                  remove.mutate(undefined, { onSuccess: () => setSaves((n) => n + 1) });
                }
              }}
              className="btn-quiet focus-ring cursor-pointer text-[11.5px]"
              style={{ color: 'var(--danger)' }}
            >
              Remove stored credentials
            </button>
          ) : null}
          {dirty && saved.source !== 'none' ? (
            <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
              Save first — the test uses what is stored.
            </span>
          ) : null}
        </span>

        {save.isError ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(save.error)}
          </span>
        ) : null}
        {remove.isError ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(remove.error)}
          </span>
        ) : null}

        {test.data && !dirty ? (
          <span
            id={testResultId}
            role="status"
            className="flex items-center gap-2 text-[11.5px]"
            style={{ color: test.data.ok ? 'var(--ok)' : 'var(--warn)' }}
          >
            <span
              aria-hidden="true"
              className="h-[5px] w-[5px] flex-none rounded-full"
              style={{ background: 'currentColor' }}
            />
            {test.data.message}
          </span>
        ) : test.isError && !dirty ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(test.error)}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'warn'; children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px]"
      style={{ color: `var(--${tone})`, borderColor: 'var(--line2)' }}
    >
      <span
        aria-hidden="true"
        className="h-[5px] w-[5px] rounded-full"
        style={{ background: 'currentColor' }}
      />
      {children}
    </span>
  );
}

function Notice({ tone, children }: { tone: 'warn'; children: React.ReactNode }) {
  return (
    <span
      className="flex items-start gap-2.5 rounded-lg border border-dashed px-[11px] py-[9px] text-[11.5px] leading-[1.5]"
      style={{ background: 'var(--in)', borderColor: 'var(--line2)', color: 'var(--muted)' }}
    >
      <span
        aria-hidden="true"
        className="mt-[5px] h-[5px] w-[5px] flex-none rounded-full"
        style={{ background: `var(--${tone})` }}
      />
      <span>{children}</span>
    </span>
  );
}
